import {
    filters as filtersBlank,
    searchRequest as searchRequestBlank,
    searchResponse as searchResponseBlank,
} from '~/utils/tours/tours-blank-states'
import {runtimeStore, toursRuntimeStore} from '@/composables/store-accessor'
import {appInstance} from '@/utils/app-accessor'
import {UTCToDate} from '@/utils/helpers'
import {salesTermsRsTransformer} from '@/utils/api-helpers'
import {isAfter, isBefore, parseISO} from 'date-fns'
import type {Ref} from 'vue'
import {useProductStore} from '~/composables/product-store'

const _singleTourSearchResponse = () => ({
    offers: [],
})

export const useToursStore = defineStore('tours', () => {
    function _getBasketItem(offerKey: any) {
        return (
            basket.value.find(item => item.offerKey === offerKey) || {
                searchRequest: searchRequestBlank(),
                offer: {info: {}},
            }
        )
    }

    const searchExpirationTime: Ref = ref(null)
    const searchRequest = ref(searchRequestBlank())
    const searchResponse = ref(searchResponseBlank())
    const sortFnName = ref('dateAsc')
    const groupOffersBy = ref('tourName')
    const filters: Ref = ref(filtersBlank())
    const singleTourSearchExpirationTime: Ref = ref(null)
    const singleTourSearchRequest: Ref = ref(null)
    const singleTourSearchResponse: Ref = ref(_singleTourSearchResponse())

    const prepareBookRequest: Ref = ref({})
    const prepareBookResponse = ref({})

    const basket: Ref<any[]> = ref([])

    function NEW_SEARCH(_searchRequest: any) {
        searchExpirationTime.value = null
        searchResponse.value = searchResponseBlank()
        searchRequest.value = _searchRequest
        singleTourSearchRequest.value = _searchRequest
        singleTourSearchResponse.value = _singleTourSearchResponse()
        singleTourSearchExpirationTime.value = null
    }

    function RESET() {
        searchExpirationTime.value = null
        searchResponse.value = searchResponseBlank()
        searchRequest.value = searchRequestBlank()
        singleTourSearchRequest.value = null

        prepareBookRequest.value = {}
        prepareBookResponse.value = {}
    }

    function NEW_SINGLE_TOUR_SEARCH(searchRequest: any) {
        singleTourSearchRequest.value = searchRequest
        singleTourSearchResponse.value = _singleTourSearchResponse()
        singleTourSearchExpirationTime.value = null
    }

    function SET_SINGLE_TOUR_SEARCH_REQUEST(rq: any) {
        singleTourSearchRequest.value = rq
    }

    function SET_SINGLE_TOUR_SEARCH_RESPONSE(rs: any) {
        singleTourSearchResponse.value = rs
    }

    function SET_SEARCH_REQUEST(rq: any) {
        searchRequest.value = rq
    }

    function SET_SEARCH_RESPONSE(rs: any) {
        searchResponse.value = rs
    }

    function SET_SINGLE_TOUR_SEARCH_EXPIRATION_TIME(date: any) {
        singleTourSearchExpirationTime.value = date
    }

    function SET_PREPARE_BOOK_REQUEST(_prepareBookRequest: any) {
        prepareBookRequest.value = _prepareBookRequest
    }

    function SET_PREPARE_BOOK_REQUEST_PROP({prop, val}: any) {
        prepareBookRequest.value[prop] = val
    }

    function SET_PREPARE_BOOK_RESPONSE(_prepareBookResponse: any) {
        prepareBookResponse.value = _prepareBookResponse
    }

    function ADD_TO_BASKET(offerKey: any) {
        basket.value = basket.value.filter(item => isBefore(new Date(), UTCToDate(parseISO(item.offer.expirationTime))))
        if (_getBasketItem(offerKey).offerKey) return

        let offer, _searchRequest
        if (singleTourSearchResponse.value)
            offer = singleTourSearchResponse.value.offers.find((offer: any) => offer.offerKey === offerKey)
        if (offer) _searchRequest = Object.assign({}, singleTourSearchRequest.value)
        else {
            offer = toursRuntimeStore.offers.find((offer: any) => offer.offerKey === offerKey)
            _searchRequest = Object.assign({}, {...searchRequest.value, packageTourId: offer.info.id})
        }

        basket.value.push({
            offerKey,
            offer,
            searchRequest: _searchRequest,
        })
    }

    function REFRESH_BASKET_PRICE({offerKey, prepareBookResponse}: any) {
        const basketItem = _getBasketItem(offerKey)
        if (!basketItem.offerKey) return
        const offer = basketItem.offer
        const {price} = salesTermsRsTransformer(prepareBookResponse.currentSalesTerms)
        offer.currentPrice = price
    }

    function RESET_FILTERS() {
        filters.value = filtersBlank()
    }

    function SET_FILTER({key, value}: any) {
        filters.value[key] = value
    }

    function SET_FILTERS(val: any) {
        filters.value = val
    }

    function SET_SORT_FN_NAME(sort: any) {
        sortFnName.value = sort
    }

    function SET_GROUP_OFFERS_BY(val: any) {
        groupOffersBy.value = val
    }

    //TODO Need refactoring
    function UPDATE_OFFER({offerKey, selectedOfferKey, selectedOfferKeys}: any) {
        const setSelected = (index: any, entryIndex: any) => {
            singleTourSearchResponse.value.offers[index].entries[entryIndex].selectedOfferKey = selectedOfferKey
            if (selectedOfferKeys) {
                singleTourSearchResponse.value.offers[index].entries[entryIndex].selectedOfferKeys = selectedOfferKeys
            }
        }
        const productOffersAdapter = (product: any) =>
            product.offers?.length ? product.offers : product.packageOffers?.length ? product.packageOffers : [product]
        singleTourSearchResponse.value.offers.forEach((tour: any, index: any) => {
            if (tour.offerKey === offerKey) {
                const entryIndex = tour.entries.findIndex((entry: any) => {
                    if (entry.type === 'CAR') {
                        return entry.offer.offerKey === selectedOfferKey
                    } else {
                        return entry.offers?.find((offer: any) => {
                            if (offer.rooms) {
                                if (!selectedOfferKeys) {
                                    return offer.rooms?.find(
                                        (room: any) => room.groupedOffers[0].offerKey === selectedOfferKey
                                    )
                                } else {
                                    return offer.rooms?.find((room: any) =>
                                        selectedOfferKeys.includes(room.groupedOffers[0].offerKey)
                                    )
                                }
                            } else {
                                return productOffersAdapter(offer).find(
                                    (offer: any) => offer.offerKey === selectedOfferKey
                                )
                            }
                        })
                    }
                })
                if (entryIndex !== -1) {
                    if (singleTourSearchResponse.value.offers[index].entries[entryIndex].obligatory) {
                        setSelected(index, entryIndex)
                        return false
                    } else if (!singleTourSearchResponse.value.offers[index].entries[entryIndex].obligatory) {
                        const key = singleTourSearchResponse.value.offers[index].entries[entryIndex].selectedOfferKey
                        if (key && key !== selectedOfferKey) {
                            setSelected(index, entryIndex)
                        } else if (!key) {
                            setSelected(index, entryIndex)
                        } else if (selectedOfferKeys) {
                            setSelected(index, entryIndex)
                        }
                        return false
                    }
                }
            }
        })
    }

    //TODO Need refactoring
    function DELETE_OFFER_TOUR({offerKey, selectedOfferKey}: any) {
        const productOffersAdapter = (product: any) =>
            product.offers?.length ? product.offers : product.packageOffers?.length ? product.packageOffers : [product]
        singleTourSearchResponse.value.offers.forEach((tour: any, index: any) => {
            if (tour.offerKey === offerKey) {
                const entryIndex = tour.entries.findIndex((entry: any) => {
                    if (entry.type === 'CAR') {
                        return entry.offer.offerKey === selectedOfferKey
                    } else {
                        return entry.offers?.find((offer: any) => {
                            if (offer.rooms) {
                                return offer.rooms?.find(
                                    (room: any) => room.groupedOffers[0].offerKey === selectedOfferKey
                                )
                            } else {
                                return productOffersAdapter(offer).find(
                                    (offer: any) => offer.offerKey === selectedOfferKey
                                )
                            }
                        })
                    }
                })
                if (entryIndex !== -1 && !singleTourSearchResponse.value.offers[index].entries[entryIndex].obligatory) {
                    delete singleTourSearchResponse.value.offers[index].entries[entryIndex].selectedOfferKey
                }
            }
        })
    }

    function SET_PREPARE_BOOK_ENTRY_PARAM_PROP({entryName, prop, val}: any) {
        const index = prepareBookRequest.value.entryParams.findIndex((entry: any) => entry.entryName === entryName)
        prepareBookRequest.value.entryParams[index][prop] = val
    }

    async function prepareBook(rq: any) {
        runtimeStore.SET_BOOKING_ACTIVE(true)
        try {
            SET_PREPARE_BOOK_REQUEST(rq)
            const rs = await appInstance.$api.preparePackageTourBook.post(rq)
            SET_PREPARE_BOOK_RESPONSE(rs)
            return rs
        } finally {
            runtimeStore.SET_BOOKING_ACTIVE(false)
        }
    }

    const hasOffers = computed(() => {
        return !!searchResponse.value.offers.length
    })

    const hasSingleTourOffers = computed(() => {
        return !!singleTourSearchResponse.value.searchKey
    })

    const basketItem = computed(() => {
        return (offerKey: any) => _getBasketItem(offerKey)
    })

    const extraPrice = computed(() => {
        return (price: any) => price
    })

    const isOffersExpired = computed(() => {
        return () =>
            hasOffers.value && !toursRuntimeStore.searchActive && isAfter(new Date(), searchExpirationTime.value)
    })

    const isSingleHotelOffersExpired = computed(() => {
        return () =>
            hasSingleTourOffers.value &&
            !toursRuntimeStore.singleTourSearchActive &&
            isAfter(new Date(), singleTourSearchExpirationTime.value)
    })

    return {
        ...useProductStore(searchResponse),
        searchExpirationTime,
        searchRequest,
        searchResponse,
        sortFnName,
        groupOffersBy,
        filters,
        singleTourSearchExpirationTime,
        singleTourSearchRequest,
        singleTourSearchResponse,
        prepareBookRequest,
        prepareBookResponse,
        basket,
        NEW_SEARCH,
        RESET,
        NEW_SINGLE_TOUR_SEARCH,
        SET_SINGLE_TOUR_SEARCH_REQUEST,
        SET_SINGLE_TOUR_SEARCH_RESPONSE,
        SET_SEARCH_REQUEST,
        SET_SEARCH_RESPONSE,
        SET_SINGLE_TOUR_SEARCH_EXPIRATION_TIME,
        SET_PREPARE_BOOK_REQUEST,
        SET_PREPARE_BOOK_REQUEST_PROP,
        SET_PREPARE_BOOK_RESPONSE,
        ADD_TO_BASKET,
        REFRESH_BASKET_PRICE,
        RESET_FILTERS,
        SET_FILTER,
        SET_FILTERS,
        SET_SORT_FN_NAME,
        SET_GROUP_OFFERS_BY,
        UPDATE_OFFER,
        DELETE_OFFER_TOUR,
        SET_PREPARE_BOOK_ENTRY_PARAM_PROP,
        prepareBook,
        hasOffers,
        hasSingleTourOffers,
        basketItem,
        extraPrice,
        isOffersExpired,
        isSingleHotelOffersExpired,
    }
})

// TODO [P3] Need check this HRM improvement. Relates with https://pinia.vuejs.org/cookbook/hot-module-replacement.html#HMR-Hot-Module-Replacement-
if (import.meta.hot) import.meta.hot.accept(acceptHMRUpdate(useToursStore, import.meta.hot))
